import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from 'src/app/core/services/payment.service';
import { UtilService } from 'src/app/core/services/util.service';
import { FeeSummary } from 'src/app/core/models/fee.model';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent implements OnInit {
  feeSummary: FeeSummary = new FeeSummary();

  constructor(
    private paymentService: PaymentService,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['appId'] && params['appId'] !== '') {
        // If we route with appId, this is the initial payment
        let appId = params['appId'];
        let feeSummaryId = params['feeSummaryId'];
        this.paymentService
          .getFeeSummary(appId, feeSummaryId)
          .subscribe((val) => {
            this.feeSummary = val;
            if (this.feeSummary.token && this.feeSummary.transactionId != null) {
              // We have already processed this fee summary - re-verify via new API
              this.paymentService.verifyAppLinkedPayment(
                this.feeSummary.token,
                this.feeSummary.feeSummaryId,
                this.feeSummary.appId
              ).subscribe({
                next: (result: any) => {
                  if (result?.success && result?.isOrderComplete) {
                    this.util.openToastr('success', 'Payment Successful');
                    this.router.navigate(['app/app-wizard', this.feeSummary.appId]);
                  }
                  // If paymentPending or not complete, user can retry payment via UI
                },
                error: (err) => {
                  console.error('Re-verification error:', err);
                  // Allow user to retry via UI
                }
              });
            }
          });
      } else if (params['token']) {
        this.afterPayment(params);
      }
    });
  }

  afterPaymentGetFeeSummary(params:any){
    // Query backend by token - this is the source of truth
    // Backend lookup returns FeeSummary with feeSummaryId, appId, isStandalone
    return this.paymentService.getFeeSummaryBytoken(params['token']);
  }

  afterPayment(params:any){
    // Callback from payment provider - query backend to get FeeSummary by token
    const token = params['token'];
    this.afterPaymentGetFeeSummary(params).subscribe({
      next:(res:FeeSummary)=>{
        if (res) {
          this.processPaymentCallback(res, token);
        } else {
          console.error('No fee summary found for token');
          this.util.openToastr('error', 'Payment information not found');
          this.router.navigate(['app/payment/error'], { queryParams: { token } });
        }
      },
      error: (err) => {
        console.error('Backend lookup failed:', err);
        this.util.openToastr('error', 'Unable to retrieve payment information');
        this.router.navigate(['app/payment/error'], { queryParams: { token } });
      }
    });
  }

  private processPaymentCallback(feeSummary: FeeSummary, token: string) {
    this.feeSummary = feeSummary;
    this.feeSummary.token = token;

    // Determine payment type and verify accordingly (isStandalone from backend)
    if (this.feeSummary.isStandalone) {
      // Standalone payment - verify via standalone API
      this.paymentService.verifyStandalonePayment(token, this.feeSummary.feeSummaryId, this.feeSummary.appId).subscribe({
        next: (result: any) => {
          if (result?.success && result?.isOrderComplete) {
            this.util.openToastr('success', 'Payment Successful');
            this.router.navigate(['app/standalone-payment/confirmation'], {
              queryParams: { feeSummaryId: this.feeSummary.feeSummaryId, appId: this.feeSummary.appId }
            });
          } else if (result?.paymentPending) {
            // Payment not completed yet - redirect back to standalone payment page
            this.router.navigate(['app/standalone-payment']);
          } else {
            this.util.openToastr('error', result?.errorMessage || 'Payment verification failed');
            this.router.navigate(['app/payment/error'], { queryParams: { token } });
          }
        },
        error: (err) => {
          console.error('Standalone payment verification error:', err);
          this.util.openToastr('error', 'Error verifying payment');
          this.router.navigate(['app/payment/error'], { queryParams: { token } });
        }
      });
    } else {
      // App-linked payment - verify via new API
      this.paymentService.verifyAppLinkedPayment(
        token,
        this.feeSummary.feeSummaryId,
        this.feeSummary.appId
      ).subscribe({
        next: (result: any) => {
          if (result?.success && result?.isOrderComplete) {
            this.util.openToastr('success', 'Payment Successful');
            this.router.navigate(['app/app-wizard', this.feeSummary.appId]);
          } else if (result?.paymentPending) {
            // Payment not completed yet (user cancelled or left Tyler page)
            // Redirect back to fee payment page to let user retry
            this.router.navigate(['app/payment'], {
              queryParams: { appId: this.feeSummary.appId, feeSummaryId: this.feeSummary.feeSummaryId }
            });
          } else {
            this.util.openToastr('error', result?.errorMessage || 'Payment verification failed');
            this.router.navigate(['app/payment/error'], { queryParams: { token } });
          }
        },
        error: (err) => {
          console.error('App-linked payment verification error:', err);
          this.util.openToastr('error', 'Error verifying payment');
          this.router.navigate(['app/payment/error'], { queryParams: { token } });
        }
      });
    }
  }

  preparePayment() {
    // Backend handles transactionId generation, line items construction, and stores token
    this.paymentService.prepareAppLinkedPayment(
      this.feeSummary.feeSummaryId,
      this.feeSummary.appId
    ).subscribe({
      next: (result: any) => {
        if (result?.success && result?.redirectUrl) {
          // Redirect to Tyler - backend has already saved token to FeeSummary
          document.location.href = result.redirectUrl;
        } else if (result?.fullyCoveredByCredits) {
          // Payment fully covered by TEACH credits - no redirect needed
          this.util.openToastr('success', 'Payment completed using available credits');
          this.router.navigate(['app/app-wizard', this.feeSummary.appId]);
        } else {
          this.util.openToastr('error', result?.errorMessage || 'Failed to prepare payment');
        }
      },
      error: (err) => {
        console.error('Error preparing payment:', err);
        this.util.openToastr('error', 'Error preparing payment. Please try again.');
      }
    });
  }

  // Legacy method - kept for backward compatibility but no longer used
  // All payment flows now use prepareAppLinkedPayment/verifyAppLinkedPayment
  // or verifyStandalonePayment directly
  callPaymentService(url: string) {
    console.warn('callPaymentService is deprecated - using new payment API');
    // Redirect to appropriate new flow
    if (this.feeSummary.token) {
      this.processPaymentCallback(this.feeSummary, this.feeSummary.token);
    }
  }

  calculateBalance() {
    //return Math.abs(this.payInfo?.feeSummary?.balanceAmount || 0);
    return this.feeSummary.balanceAmount || 0;
  }
}
